import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  MapPin,
  User,
  CalendarDays,
  RefreshCw,
  Bell,
  FileText,
  Edit2,
  CheckCircle2,
  Clock,
  History
} from 'lucide-react';

const STATUS_CONFIG = {
  overdue: { label: 'Te laat', badgeClass: 'bg-red-50 text-red-700 border-red-200' },
  due_soon: { label: 'Binnenkort', badgeClass: 'bg-orange-50 text-orange-700 border-orange-200' },
  upcoming: { label: 'Aankomend', badgeClass: 'bg-blue-50 text-blue-700 border-blue-200' },
  completed: { label: 'Afgerond', badgeClass: 'bg-green-50 text-green-700 border-green-200' },
};

const TYPE_LABELS = {
  keuring: 'Keuring',
  contract: 'Contract',
  onderhoud: 'Onderhoud',
  voertuig: 'Voertuig',
  vergunning: 'Vergunning',
  betaling: 'Betaling',
};

const RECURRENCE_LABELS = {
  'one-time': 'Eenmalig',
  'monthly': 'Maandelijks',
  'quarterly': 'Per kwartaal',
  'half-yearly': 'Halfjaarlijks',
  'yearly': 'Jaarlijks',
};

function formatDate(dateStr) {
  if (!dateStr) return '—';
  try {
    return new Date(dateStr).toLocaleDateString('nl-NL', {
      day: 'numeric', month: 'long', year: 'numeric'
    });
  } catch {
    return dateStr;
  }
}

function formatDateTime(dateStr) {
  if (!dateStr) return '—';
  try {
    return new Date(dateStr).toLocaleString('nl-NL', {
      day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  } catch {
    return dateStr;
  }
}

export default function ComplianceDetail({ open, onOpenChange, item, locationMap, userMap, onEdit, onComplete }) {
  if (!item) return null;

  const statusCfg = STATUS_CONFIG[item.status] || STATUS_CONFIG.upcoming;
  const recurrenceLabel = RECURRENCE_LABELS[item.recurrence?.interval] || 'Eenmalig';
  const history = item.history || [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-hidden flex flex-col" data-testid="compliance-item-detail-dialog">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <DialogTitle className="font-heading flex-1">{item.title}</DialogTitle>
            <Badge variant="outline" className={statusCfg.badgeClass} data-testid="compliance-item-detail-status">
              {statusCfg.label}
            </Badge>
          </div>
          <DialogDescription className="text-xs">
            {TYPE_LABELS[item.type] || item.type} &middot; Aangemaakt {formatDate(item.created_at)}
          </DialogDescription>
        </DialogHeader>

        <ScrollArea className="flex-1 -mx-6 px-6">
          <div className="space-y-4 pb-4">
            {/* Summary Block */}
            <div className="grid grid-cols-2 gap-3">
              <InfoRow icon={MapPin} label="Locatie" value={locationMap[item.location_id] || item.location_id} />
              <InfoRow icon={User} label="Toegewezen aan" value={userMap[item.assigned_to] || item.assigned_to} />
              <InfoRow icon={CalendarDays} label="Vervaldatum" value={formatDate(item.due_date)} testId="compliance-item-detail-due-date" />
              <InfoRow icon={RefreshCw} label="Herhaling" value={recurrenceLabel} />
              <InfoRow icon={Bell} label="Herinneringen" value={(item.reminder_days || []).join(', ') + ' dagen'} />
              {item.completed_at && (
                <InfoRow icon={CheckCircle2} label="Afgerond op" value={formatDateTime(item.completed_at)} />
              )}
            </div>

            {/* Description */}
            {item.description && (
              <>
                <Separator />
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-slate-500 font-medium mb-1 flex items-center gap-1">
                    <FileText className="w-3 h-3" /> Omschrijving
                  </p>
                  <p className="text-sm text-slate-700 leading-relaxed">{item.description}</p>
                </div>
              </>
            )}

            {/* Completion notes */}
            {item.completed_notes && (
              <>
                <Separator />
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-slate-500 font-medium mb-1 flex items-center gap-1">
                    <CheckCircle2 className="w-3 h-3" /> Afrondnotitie
                  </p>
                  <p className="text-sm text-slate-700 leading-relaxed">{item.completed_notes}</p>
                </div>
              </>
            )}

            {/* History */}
            {history.length > 0 && (
              <>
                <Separator />
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-slate-500 font-medium mb-2 flex items-center gap-1">
                    <History className="w-3 h-3" /> Geschiedenis ({history.length})
                  </p>
                  <div className="space-y-2">
                    {history.map((h, idx) => (
                      <div key={idx} className="p-2.5 rounded-lg bg-slate-50 border border-slate-100">
                        <div className="flex items-center justify-between">
                          <span className="text-xs font-medium text-slate-700">{formatDateTime(h.date)}</span>
                          <span className="text-[10px] text-slate-500">{h.completed_by}</span>
                        </div>
                        {h.notes && <p className="text-xs text-slate-600 mt-1">{h.notes}</p>}
                        {h.due_date && <p className="text-[10px] text-slate-400 mt-0.5">Deadline: {formatDate(h.due_date)}</p>}
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}
          </div>
        </ScrollArea>

        {/* Actions */}
        <div className="flex items-center gap-2 pt-2 border-t border-slate-100">
          <Button variant="outline" size="sm" onClick={() => onEdit(item)} className="gap-1.5">
            <Edit2 className="w-3.5 h-3.5" /> Bewerken
          </Button>
          {item.status !== 'completed' && (
            <Button
              size="sm"
              onClick={() => onComplete(item)}
              className="gap-1.5 bg-green-600 hover:bg-green-700 text-white"
            >
              <CheckCircle2 className="w-3.5 h-3.5" /> Afronden
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function InfoRow({ icon: Icon, label, value, testId }) {
  return (
    <div data-testid={testId}>
      <p className="text-[10px] uppercase tracking-wider text-slate-500 font-medium mb-0.5 flex items-center gap-1">
        <Icon className="w-3 h-3" /> {label}
      </p>
      <p className="text-sm text-slate-800">{value}</p>
    </div>
  );
}
